import {
  DropdownButtonType,
  InlineAlertType,
  InlineAlertWidget,
  ListItem,
  LocallyFilteringList,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  SingleSelectDropdown,
  TextLinkWidget
} from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { classes, style } from 'typestyle';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { PulseDigestSchedule } from '~src/settings/general-settings/components/sections/pulse/PulseDigestSchedule';
import {
  dropdownListStyle,
  learnMoreStyle,
  settingsRadioButtonItemHeight,
  SettingsStyles
} from '~src/settings/styles/CommonStyles';
import { Groups } from '~src/userManagement/groups/Groups';
import { IGroupInfo } from '~src/userManagement/groups/models/IGroupInfo';

import {
  CompositeSessionInfoSelectors,
  SessionServerSelectors
} from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

const subSettingsWrapper = style({
  display: 'flex',
});

export const ALLOW_PULSE_FEATURE_CHECKBOX = 'allow-pulse-feature-checkbox';
export const ALLOW_PULSE_FEATURE_SITE_SETTING = 'settings.pulseEnabled';
export const ALLOW_PULSE_GROUP_SITE_SETTING = 'settings.pulseGroupId';
export const DIGEST_START_TIME_SITE_SETTING = 'settings.siteSettingsV2.pulseDigestStartTime';
export const ALLOW_PULSE_GROUP_BUTTON_SELECTED = 'allowPulseGroupButtonSelected';

export const ALLOW_PULSE_DEFAULT_GROUP_RADIO_BUTTON: String = undefined; //use undefined instead of null as required by the component
export const ALLOW_PULSE_GROUP_SPECIFIED_RADIO_BUTTON = 'allow-pulse-group-specified-radio-button-value';
export const ALLOW_PULSE_FEATURE_SITE_SETTING_TEST_ID = 'allow-pulse-feature-test-id';
export const ALLOW_PULSE_GROUP_SITE_SETTING_TEST_ID = 'allow-pulse-group-test-id';
export const ALLOW_PULSE_GROUP_ALL_RADIO_BUTTON_TEST_ID = 'allow-pulse-group-all-radio-button-test-id';
export const ALLOW_PULSE_GROUP_SPECIFIC_RADIO_BUTTON_TEST_ID = 'allow-pulse-group-specific-radio-button-test-id';
export const ALLOW_PULSE_GROUP_DELETED_WARNING_TEST_ID = 'allow-pulse-group-deleted-warning-test-id';
export const PULSE_DESCRIPTION_LEARN_MORE_LINK_TEST_ID = 'pulse-description-learn-more-link-test-id';
export const ALLOW_PULSE_GROUP_SPECIFIED_WARNING_TEST_ID = 'allow-pulse-group-specified-warning-test-id';

export type AllowPulseFeatureSectionProps = SiteSettingsForSiteAdminProps<'pulseEnabled' | 'pulseGroupId' | 'siteSettingsV2' | 'useDefaultTimeZone' | 'timeZone'> & { allowPulseGroupButtonSelected?: boolean, timeZonesListItems?: ListItem[] };

export function AllowPulseFeatureSection(props: AllowPulseFeatureSectionProps): React.ReactElement {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();
  setUpInitialButtonStates(formik);
  const showSettings = useShowSettingsSection();
  if (!showSettings) {
    return null;
  }

  if (FeatureFlags.ntbue_Default_Send_TimeEnabled) {
    return (
      <SettingsSection title={Messages.pulseSiteSettingsHeading()}
                       testId={'allow-pulse-feature-section'}
                       description={
                         <div>
                           {Messages.pulseSiteSettingsDescription()}

                         </div>
                       }>
        <div className={SettingsStyles.settingsSubGroupNameProperties}>{Messages.allowPulseDeploymentTitle()}</div>
        <p>
          {Messages.allowPulseDeploymentDetails()}
          &nbsp;
          <TextLinkWidget
            testId={PULSE_DESCRIPTION_LEARN_MORE_LINK_TEST_ID}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getHelpUrlByCategory('online-pulse-setting')}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </p>
        <div style={{marginBottom: '12px'}} className={SettingsStyles.checkboxGroup}>
          {AllowPulseFeatureCheckbox()}
          {AllowPulseGroupRadioGroupAndDropDown(formik)}
        </div>
        <div className={SettingsStyles.settingsSubGroupNameProperties}>
          {Messages.pulseDigestScheduleSiteSettingHeading()}
        </div>
        {PulseDigestSchedule(formik)}
      </SettingsSection>
    );
  }

  return (
    <SettingsSection title={Messages.allowPulseFeatureTitle()}
                     testId={'allow-pulse-feature-section'}
                     description={
                       <div>
                         {Messages.allowPulseFeatureDetails()}
                         &nbsp;
                         <TextLinkWidget
                           testId={PULSE_DESCRIPTION_LEARN_MORE_LINK_TEST_ID}
                           target={'_blank'}
                           className={learnMoreStyle}
                           href={HelpService.getHelpUrlByCategory('online-pulse-setting')}>
                           {Messages.learnMore()}
                         </TextLinkWidget>
                       </div>
                     }>
      <div className={SettingsStyles.checkboxGroup}>
        {AllowPulseFeatureCheckbox()}
        {AllowPulseGroupRadioGroupAndDropDown(formik)}
      </div>
    </SettingsSection>
  );
}

function renderInlineAlertForGroupSelected(): JSX.Element {
  const learnMoreButton = (
    <TextLinkWidget
      testId={PULSE_DESCRIPTION_LEARN_MORE_LINK_TEST_ID}
      target={'_blank'}
      className={learnMoreStyle}
      href={HelpService.getHelpUrlByCategory('online-pulse-setting')}>
      {Messages.learnMore()}
    </TextLinkWidget>
  );

  return (
    <InlineAlertWidget
      style={{ marginTop: 16 }}
      button={learnMoreButton}
      children={Messages.allowPulseGroupSpecificGroup_AlertText()}
      type={InlineAlertType.Warning}
      testId={ALLOW_PULSE_GROUP_SPECIFIED_WARNING_TEST_ID}
    />
  );
}

function setUpInitialButtonStates(formik: FormikContextType<AllowPulseFeatureSectionProps>) {
  // Set up some additional states around button click so that we can block Save
  if (formik.values.allowPulseGroupButtonSelected === undefined) {
    formik.resetForm({values: {...formik.values, allowPulseGroupButtonSelected: formik.values.settings.pulseGroupId !== undefined}});
  }
  else if (formik.values.allowPulseGroupButtonSelected === true && formik.values.settings.pulseGroupId === undefined && formik.values.settings.pulseEnabled) {
    formik.setErrors({allowPulseGroupButtonSelected: 'No group selected'});
  }
}

function useShowSettingsSection(): boolean {
  const isOnlineEnvironment = !!useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteOrServerAdmin = !!useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  return isOnlineEnvironment && isSiteOrServerAdmin;
}

async function getGroups(): Promise<IGroupInfo[]> {
  let allGroups: IGroupInfo[] = [];
  let moreItems: boolean = true;
  let startIndex: number = 0;
  const GET_GROUPS_PAGE_SIZE = 600;

  while (moreItems) {
    const pagedGroups = await Groups.all().getSlice(startIndex, GET_GROUPS_PAGE_SIZE);
    allGroups = allGroups.concat(pagedGroups.items);
    startIndex = startIndex + GET_GROUPS_PAGE_SIZE;
    moreItems = (startIndex < pagedGroups.total);
  }
  return allGroups;
}

function AllowPulseFeatureCheckbox(): JSX.Element {
  return (
    <div className={classes(SettingsStyles.checkboxGroup)}>
      <FormikCheckbox
        name={ALLOW_PULSE_FEATURE_SITE_SETTING}
        label={Messages.allowPulseFeatureCheckBox()}
        testId={ALLOW_PULSE_FEATURE_SITE_SETTING_TEST_ID}
      />
    </div>
  );
}

function AllowPulseGroupRadioGroupAndDropDown(formik: FormikContextType<AllowPulseFeatureSectionProps>): JSX.Element {
  const [groups, setGroups] = React.useState<IGroupInfo[]>([]);

  React.useEffect(() => {
    const getAllGroups = async () => {
      const groups = await getGroups();
      setGroups(groups);
    };

    getAllGroups();
  }, []);

  const buttons = React.useMemo(() => getAllowGroupButtonsList(formik, groups), [formik, groups]);
  const initRadioButton = React.useRef<string>(formik.values.settings.pulseGroupId);

  function handleButtonChange(value: string) {
    // Only allow Save when 
    // 1. value === undefined: this is when user clicks on the Allow All Users option
    // 2. PulseAllowGroup !== undefined, meaning that PulseAllowGroup is a real value not "No group selected"

    const allowAllUsersRadioButtonSelected = value === undefined;
    if (allowAllUsersRadioButtonSelected) {
      formik.setFieldTouched(ALLOW_PULSE_GROUP_SITE_SETTING, initRadioButton.current !== value);
      formik.setFieldValue(ALLOW_PULSE_GROUP_SITE_SETTING, undefined);
      formik.setFieldValue(ALLOW_PULSE_GROUP_BUTTON_SELECTED, false);
    }
    else if (formik.values.settings.pulseGroupId !== undefined) {
      formik.setFieldTouched(ALLOW_PULSE_GROUP_SITE_SETTING, initRadioButton.current !== value);
      formik.setFieldValue(ALLOW_PULSE_GROUP_SITE_SETTING, formik.values.settings.pulseGroupId);
      formik.setFieldValue(ALLOW_PULSE_GROUP_BUTTON_SELECTED, true);
    } else {
      formik.setFieldValue(ALLOW_PULSE_GROUP_BUTTON_SELECTED, true);
      formik.setErrors({allowPulseGroupButtonSelected: 'No group selected'}); // Error message is not exposed to user, but causes Save button to be disabled
    }
  }

  return (
    <div className={SettingsStyles.settingsSubGroup}>
      <div className={subSettingsWrapper}>
        <RadioButtonWithLabelGroupWidget
          buttonList={buttons}
          className={settingsRadioButtonItemHeight}
          removeLeftMargin
          name={ALLOW_PULSE_GROUP_SITE_SETTING}
          testId={ALLOW_PULSE_GROUP_SITE_SETTING_TEST_ID}
          checkedIndex={formik.values.allowPulseGroupButtonSelected ? 1 : 0}
          handleChange={(value: string) => handleButtonChange(value)}
        />
      </div>
    </div>
  );
}

function getAllowGroupButtonsList(formik: FormikContextType<AllowPulseFeatureSectionProps>, groups: IGroupInfo[]): RadioButtonWithLabelOption<String>[] {
   // When the All Users option is clicked, return undefined to distinguish it from when the second Only Groups option where we return an empty string
  function handleDropdownChange(value: string) {
    if (value === Messages.pulseAllowGroupNonSelected()) {
      return;
    }
    formik.setFieldValue(ALLOW_PULSE_GROUP_SITE_SETTING, value);
  }

  return [
    {
      value: ALLOW_PULSE_DEFAULT_GROUP_RADIO_BUTTON,
      label: Messages.allowPulseGroupForAll(),
      testId: ALLOW_PULSE_GROUP_ALL_RADIO_BUTTON_TEST_ID,
      disabled: !formik.values.settings.pulseEnabled
    },
    {
      value: ALLOW_PULSE_GROUP_SPECIFIED_RADIO_BUTTON,
      label: Messages.allowPulseGroupSpecificGroup(),
      testId: ALLOW_PULSE_GROUP_SPECIFIC_RADIO_BUTTON_TEST_ID,
      disabled: !formik.values.settings.pulseEnabled,
      detail: AllowPulseFeatureGroupDropdown(formik, handleDropdownChange, groups)
    }
  ];
}

function AllowPulseFeatureGroupDropdown(formik: FormikContextType<AllowPulseFeatureSectionProps>, handleDropdownChange: any, groups: IGroupInfo[]): JSX.Element {
  let previousGroupDeleted = false;

  function getPulseAllowGroup(PulseAllowGroupLuid: string): string {
    if (!PulseAllowGroupLuid) return Messages.pulseAllowGroupSelectGroup();

    if (groups.find(g => g.groupLuid === PulseAllowGroupLuid)) {
      return PulseAllowGroupLuid;
    } else {
      // If the groupLuid is not undefined but cannot match any of the exiting group, it means that it's an orphaned value
      previousGroupDeleted = true;
      return Messages.pulseAllowGroupNonSelected();
    }
  }

  const groupListItems = groups.map(group => ({
    content: group.name,
    tooltip: group.name,
    value: group.groupLuid
  }));

  // By default the filtering list would filter on value (project ID), but we want to filter on name
  // We use the tooltip instead of content because the content string may be elided if the name is long
  const groupFilterFn = (item: ListItem, filter: string) => item.tooltip?.toLowerCase().includes(filter.toLowerCase());

  let PulseAllowGroupLuid = getPulseAllowGroup(formik.values.settings.pulseGroupId);
  if (!PulseAllowGroupLuid) {
    PulseAllowGroupLuid = Messages.pulseAllowGroupNonSelected();
  }

  return (
    <>
      <SingleSelectDropdown
        list={<LocallyFilteringList style={{maxWidth: '70vw'}} items={groupListItems} itemFilterFn={groupFilterFn} className={dropdownListStyle} />}
        type={DropdownButtonType.Outline}
        onChange={(value: string) => handleDropdownChange(value)}
        value={PulseAllowGroupLuid}
        label=''
        disabled={!formik.values.settings.pulseEnabled || !formik.values.allowPulseGroupButtonSelected}
      />
      {previousGroupDeleted && <InlineAlertWidget testId={ALLOW_PULSE_GROUP_DELETED_WARNING_TEST_ID} type={InlineAlertType.Warning} style={{ marginBottom: 10 }}>
        {Messages.pulseAllowGroupDeleted()}
      </InlineAlertWidget>}
      {formik.values.settings.pulseEnabled && formik.values.settings.pulseGroupId !== undefined && formik.dirty && formik.initialValues.settings.pulseEnabled && renderInlineAlertForGroupSelected()}
    </>
  );
}

/* for registering the component in settings registry */
export const AllowPulseFeatureSpecs = componentRegistryEntry({
  name: 'AllowPulseFeatureSection',
  component: AllowPulseFeatureSection
});
